import { createContext, useContext, useState, useEffect, useRef } from "react";
import { setActiveCurrency } from "../lib/currency";

const SettingsContext = createContext();

// Traducciones completas
const translations = {
  es: {
    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.newRental": "Nuevo Alquiler",
    "nav.activeRentals": "Alquileres Activos",
    "nav.returns": "Devoluciones",
    "nav.customers": "Clientes",
    "nav.providers": "Proveedores",
    "nav.inventory": "Inventario",
    "nav.tariffs": "Tarifas",
    "nav.reports": "Reportes",
    "nav.maintenance": "Mantenimiento",
    "nav.cashRegister": "Caja",
    "nav.integrations": "Integraciones",
    "nav.settings": "Configuración",
    "nav.teamManagement": "Gestión de Equipo",
    "nav.storeManagement": "Gestión de Tiendas",
    "nav.myAccount": "Mi Cuenta",
    "nav.help": "Ayuda",
    "nav.support": "Soporte",
    
    // Settings page
    "settings.title": "Configuración",
    "settings.subtitle": "Personaliza el comportamiento de la aplicación",
    "settings.saveChanges": "Guardar Cambios",
    "settings.saved": "Configuración guardada correctamente",
    
    // Brand Identity section
    "settings.brandIdentity": "Identidad de Marca",
    "settings.brandIdentity.desc": "Logo e idioma de la aplicación",
    "settings.logo": "Logo de la Empresa",
    "settings.logo.desc": "Aparecerá en la cabecera y en los tickets",
    "settings.logo.upload": "Subir Logo",
    "settings.logo.change": "Cambiar Logo",
    "settings.logo.remove": "Quitar Logo",
    "settings.logo.formats": "PNG, JPG o SVG. Máx 2MB",
    "settings.language": "Idioma",
    "settings.language.desc": "Selecciona el idioma de la interfaz",
    "settings.language.es": "Español",
    "settings.language.en": "English",
    "settings.language.ca": "Català",
    "settings.language.fr": "Français",
    
    // Ticket Design section
    "settings.ticketDesign": "Diseño de Ticket",
    "settings.ticketDesign.desc": "Personaliza el contenido de los tickets impresos",
    "settings.ticket.header": "Encabezado del Ticket",
    "settings.ticket.header.placeholder": "Nombre de tu negocio, dirección, teléfono...",
    "settings.ticket.footer": "Pie de Página",
    "settings.ticket.footer.placeholder": "Mensaje de agradecimiento, horarios...",
    "settings.ticket.terms": "Términos Legales",
    "settings.ticket.terms.placeholder": "Condiciones de alquiler, política de devoluciones...",
    "settings.ticket.showDni": "Mostrar DNI del Cliente",
    "settings.ticket.showDni.desc": "El DNI aparecerá en el ticket impreso",
    "settings.ticket.showVat": "Mostrar Desglose de IVA",
    "settings.ticket.showVat.desc": "Muestra base imponible e IVA separados",
    "settings.ticket.preview": "Vista Previa del Ticket",
    
    // VAT section
    "settings.vat": "Configuración de IVA",
    "settings.vat.desc": "Define el IVA aplicado a los alquileres",
    "settings.vat.default": "IVA por Defecto",
    "settings.vat.default.desc": "Este porcentaje se aplicará automáticamente a nuevos alquileres",
    "settings.vat.included": "IVA Incluido en Precios",
    "settings.vat.included.desc": "Los precios de tarifa ya incluyen el IVA",

    // Currency section
    "settings.currency": "Moneda",
    "settings.currency.desc": "Moneda operativa de la tienda (solo cambia la visualización)",
    "settings.currency.label": "Moneda de la tienda",
    "settings.currency.note": "Los importes no se convierten: solo cambia cómo se muestran. La suscripción de SkiFlow se factura siempre en EUR.",
    
    // Print section
    "settings.print": "Configuración de Impresión",
    "settings.autoPrint": "Impresión Automática de Tickets",
    "settings.autoPrint.desc": "El sistema imprimirá automáticamente los tickets al completar transacciones",
    "settings.autoPrint.enabled": "Los tickets se imprimirán automáticamente",
    "settings.autoPrint.disabled": "Los tickets requieren confirmación manual",
    "settings.autoPrint.tip": "Activa esta opción si tienes una impresora térmica conectada",
    
    // Hardware section
    "settings.hardware": "Hardware",
    "settings.hardware.desc": "Configuración de escáner e impresora",
    "settings.scanner": "Escáner / Pistola de Códigos",
    "settings.scanner.quickMode": "Modo Escaneo Rápido",
    "settings.scanner.quickMode.desc": "Añade el producto automáticamente al detectar un código exacto",
    "settings.scanner.quickMode.disabled": "Solo busca el producto y espera confirmación manual",
    "settings.printer": "Impresora",
    "settings.printer.paperWidth": "Ancho de Papel",
    "settings.printer.paperWidth.desc": "Selecciona el ancho del papel de tu impresora térmica",
    "settings.printer.80mm": "80mm (Estándar)",
    "settings.printer.58mm": "58mm (Estrecho)",
    "settings.printer.autoPrint": "Auto-Imprimir",
    "settings.printer.autoPrint.desc": "Abre el diálogo de impresión automáticamente al confirmar un pago",
    "settings.printer.doubleCopy": "Imprimir Doble Copia",
    "settings.printer.doubleCopy.desc": "Imprime dos tickets seguidos (Tienda y Cliente)",
    "settings.printer.setupInstructions.title": "Configuración de Impresora",
    "settings.printer.setupInstructions.step1": "Asegúrate de que tu impresora térmica está instalada y marcada como Predeterminada en tu ordenador.",
    "settings.printer.setupInstructions.step2": "Configura el tamaño de papel en tu impresora (58mm o 80mm) según el rollo que uses.",
    "settings.printer.setupInstructions.step3": "Para impresión sin diálogos, configura tu navegador en 'Modo Kiosco' o marca 'Imprimir en silencio' en la configuración del navegador.",
    "settings.printer.setupInstructions.note": "El sistema usa los drivers de tu sistema operativo - no requiere configuración de IP ni software adicional.",
    
    // Common
    "common.cancel": "Cancelar",
    "common.save": "Guardar",
    "common.close": "Cerrar",
    "common.loading": "Cargando...",
    "common.status": "Estado actual",
    "common.recommended": "Recomendado",
    "common.preview": "Vista Previa",
    "common.enabled": "Activado",
    "common.disabled": "Desactivado",
    
    // Dashboard
    "dashboard.title": "Panel de Control",
    "dashboard.subtitle": "Gestiona tu tienda de alquiler con información en tiempo real",
    "dashboard.newRental": "NUEVO ALQUILER",
    "dashboard.revenueToday": "Ingresos Netos Hoy",
    "dashboard.rentalsToday": "Alquileres Hoy",
    "dashboard.activeRentals": "Alquileres Activos",
    "dashboard.activeRentals.desc": "En curso ahora mismo",
    "dashboard.pendingReturns": "Devoluciones Pendientes",
    "dashboard.pendingReturns.allToday": "Todas para hoy",
    "dashboard.pendingReturns.none": "Sin pendientes",
    "dashboard.pendingReturns.todayAndOverdue": "Hoy + atrasadas",
    "dashboard.overdueReturns": "Devoluciones Atrasadas",
    "dashboard.stockOccupation": "Ocupación Stock",
    "dashboard.rentedMaterial": "Material alquilado",
    "dashboard.dailyCustomers": "Clientes del Día",
    "dashboard.uniqueCustomers": "Clientes únicos atendidos",
    "dashboard.returnsControl": "Control de Devoluciones",
    "dashboard.returnsControl.desc": "Material pendiente por recoger HOY",
    "dashboard.delay": "¡RETRASO!",
    "dashboard.items": "artículos",
    "dashboard.toReturn": "por devolver",
    "dashboard.viewList": "Ver lista",
    "dashboard.overdueWarning": "y hay material sin devolver",
    "dashboard.manageNow": "Gestionar ahora",
    "dashboard.weeklyOccupation": "Vista Semanal de Ocupación",
    "dashboard.highAvail": "Alta disp.",
    "dashboard.lowAvail": "Pocos",
    "dashboard.critical": "Crítico",
    "dashboard.deliveries": "Entregas",
    "dashboard.returnsLabel": "Devoluciones",
    "dashboard.avgAvailability": "Disponibilidad Media",
    "dashboard.inventoryPerformance": "Rendimiento de Inventario",
    "dashboard.customRange": "Rango Personalizado",
    "dashboard.selectDateRange": "Selecciona un rango de fechas",
    "dashboard.analyzeHistory": "Analiza el rendimiento histórico",
    "dashboard.applyFilter": "Aplicar Filtro",
    "dashboard.today": "Hoy",
    "dashboard.thisWeek": "Esta Semana",
    "dashboard.thisMonth": "Este Mes",
    "dashboard.mostRented": "Más Alquilados",
    "dashboard.mostProfitable": "Más Rentables",
    "dashboard.rentalsCount": "alquileres",
    "dashboard.noData": "Sin datos para este período",
    "dashboard.staleStock": "Stock Parado",
    "dashboard.idle": "sin alquilar",
    "dashboard.allStockRotating": "¡Todo el stock tiene rotación!",
    "dashboard.urgentAlerts": "Alertas Urgentes",
    "dashboard.expiredRentals": "Alquileres vencidos",
    "dashboard.viewReturns": "Ver devoluciones",
    "dashboard.maintenance": "Mantenimiento",
    "dashboard.viewEquipment": "Ver equipos",
    
    // Rentals
    "rentals.title": "Alquileres Activos",
    "rentals.new": "Nuevo Alquiler",
    "rentals.customer": "Cliente",
    "rentals.items": "Artículos",
    "rentals.days": "Días",
    "rentals.total": "Total",
    "rentals.status": "Estado",
    "rentals.actions": "Acciones",
    "rentals.changes": "CAMBIOS",
    "rentals.quickReturn": "Devolución Rápida",
    "rentals.daysRemaining": "días restantes",
    
    // Returns
    "returns.title": "Devoluciones",
    "returns.pending": "Pendientes de Devolución",
    "returns.process": "Procesar Devolución",
    "returns.manageChange": "GESTIONAR",
    
    // Ticket preview
    "ticket.rental": "TICKET DE ALQUILER",
    "ticket.date": "Fecha",
    "ticket.customer": "Cliente",
    "ticket.dni": "DNI",
    "ticket.items": "Artículos",
    "ticket.subtotal": "Subtotal",
    "ticket.vat": "IVA",
    "ticket.total": "TOTAL",
    "ticket.vatIncluded": "IVA Incluido",
    "ticket.paymentMethod": "Método de Pago",
    "ticket.cash": "Efectivo",
    "ticket.card": "Tarjeta",
    "ticket.thankYou": "¡Gracias por su confianza!",
  },
  en: {
    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.newRental": "New Rental",
    "nav.activeRentals": "Active Rentals",
    "nav.returns": "Returns",
    "nav.customers": "Customers",
    "nav.providers": "Providers",
    "nav.inventory": "Inventory",
    "nav.tariffs": "Tariffs",
    "nav.reports": "Reports",
    "nav.maintenance": "Maintenance",
    "nav.cashRegister": "Cash Register",
    "nav.integrations": "Integrations",
    "nav.settings": "Settings",
    "nav.teamManagement": "Team Management",
    "nav.storeManagement": "Store Management",
    "nav.myAccount": "My Account",
    "nav.help": "Help",
    "nav.support": "Support",
    
    // Settings page
    "settings.title": "Settings",
    "settings.subtitle": "Customize the application behavior",
    "settings.saveChanges": "Save Changes",
    "settings.saved": "Settings saved successfully",
    
    // Brand Identity section
    "settings.brandIdentity": "Brand Identity",
    "settings.brandIdentity.desc": "Logo and application language",
    "settings.logo": "Company Logo",
    "settings.logo.desc": "Will appear in the header and on tickets",
    "settings.logo.upload": "Upload Logo",
    "settings.logo.change": "Change Logo",
    "settings.logo.remove": "Remove Logo",
    "settings.logo.formats": "PNG, JPG or SVG. Max 2MB",
    "settings.language": "Language",
    "settings.language.desc": "Select the interface language",
    "settings.language.es": "Español",
    "settings.language.en": "English",
    "settings.language.ca": "Català",
    "settings.language.fr": "Français",
    
    // Ticket Design section
    "settings.ticketDesign": "Ticket Design",
    "settings.ticketDesign.desc": "Customize the content of printed tickets",
    "settings.ticket.header": "Ticket Header",
    "settings.ticket.header.placeholder": "Business name, address, phone...",
    "settings.ticket.footer": "Footer",
    "settings.ticket.footer.placeholder": "Thank you message, hours...",
    "settings.ticket.terms": "Legal Terms",
    "settings.ticket.terms.placeholder": "Rental conditions, return policy...",
    "settings.ticket.showDni": "Show Customer ID",
    "settings.ticket.showDni.desc": "ID will appear on printed ticket",
    "settings.ticket.showVat": "Show VAT Breakdown",
    "settings.ticket.showVat.desc": "Shows taxable base and VAT separately",
    "settings.ticket.preview": "Ticket Preview",
    
    // VAT section
    "settings.vat": "VAT Configuration",
    "settings.vat.desc": "Define the VAT applied to rentals",
    "settings.vat.default": "Default VAT",
    "settings.vat.default.desc": "This percentage will be automatically applied to new rentals",
    "settings.vat.included": "VAT Included in Prices",
    "settings.vat.included.desc": "Tariff prices already include VAT",

    // Currency section
    "settings.currency": "Currency",
    "settings.currency.desc": "Store operational currency (display only)",
    "settings.currency.label": "Store currency",
    "settings.currency.note": "Amounts are not converted: only the display format changes. The SkiFlow subscription is always billed in EUR.",
    
    // Print section
    "settings.print": "Print Settings",
    "settings.autoPrint": "Automatic Ticket Printing",
    "settings.autoPrint.desc": "The system will automatically print tickets when completing transactions",
    "settings.autoPrint.enabled": "Tickets will print automatically",
    "settings.autoPrint.disabled": "Tickets require manual confirmation",
    "settings.autoPrint.tip": "Enable this option if you have a thermal printer connected",
    
    // Hardware section
    "settings.hardware": "Hardware",
    "settings.hardware.desc": "Scanner and printer configuration",
    "settings.scanner": "Scanner / Barcode Gun",
    "settings.scanner.quickMode": "Quick Scan Mode",
    "settings.scanner.quickMode.desc": "Automatically adds the product when an exact code is detected",
    "settings.scanner.quickMode.disabled": "Only searches the product and waits for manual confirmation",
    "settings.printer": "Printer",
    "settings.printer.paperWidth": "Paper Width",
    "settings.printer.paperWidth.desc": "Select your thermal printer paper width",
    "settings.printer.80mm": "80mm (Standard)",
    "settings.printer.58mm": "58mm (Narrow)",
    "settings.printer.autoPrint": "Auto-Print",
    "settings.printer.autoPrint.desc": "Automatically opens print dialog when confirming a payment",
    "settings.printer.doubleCopy": "Print Double Copy",
    "settings.printer.doubleCopy.desc": "Prints two tickets in a row (Store and Customer)",
    "settings.printer.setupInstructions.title": "Printer Setup",
    "settings.printer.setupInstructions.step1": "Make sure your thermal printer is installed and set as Default on your computer.",
    "settings.printer.setupInstructions.step2": "Configure the paper size on your printer (58mm or 80mm) according to your roll.",
    "settings.printer.setupInstructions.step3": "For silent printing, set your browser to 'Kiosk Mode' or enable 'Print silently' in browser settings.",
    "settings.printer.setupInstructions.note": "The system uses your OS drivers - no IP configuration or additional software required.",
    
    // Common
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.close": "Close",
    "common.loading": "Loading...",
    "common.status": "Current status",
    "common.recommended": "Recommended",
    "common.preview": "Preview",
    "common.enabled": "Enabled",
    "common.disabled": "Disabled",
    
    // Dashboard
    "dashboard.title": "Dashboard",
    "dashboard.subtitle": "Manage your rental shop with real-time information",
    "dashboard.newRental": "NEW RENTAL",
    "dashboard.revenueToday": "Net Revenue Today",
    "dashboard.rentalsToday": "Rentals Today",
    "dashboard.activeRentals": "Active Rentals",
    "dashboard.activeRentals.desc": "Currently in progress",
    "dashboard.pendingReturns": "Pending Returns",
    "dashboard.pendingReturns.allToday": "All for today",
    "dashboard.pendingReturns.none": "No pending",
    "dashboard.pendingReturns.todayAndOverdue": "Today + overdue",
    "dashboard.overdueReturns": "Overdue Returns",
    "dashboard.stockOccupation": "Stock Occupancy",
    "dashboard.rentedMaterial": "Rented material",
    "dashboard.dailyCustomers": "Today's Customers",
    "dashboard.uniqueCustomers": "Unique customers served",
    "dashboard.returnsControl": "Returns Control",
    "dashboard.returnsControl.desc": "Material pending pickup TODAY",
    "dashboard.delay": "OVERDUE!",
    "dashboard.items": "items",
    "dashboard.toReturn": "to return",
    "dashboard.viewList": "View list",
    "dashboard.overdueWarning": "and there is unreturned material",
    "dashboard.manageNow": "Manage now",
    "dashboard.weeklyOccupation": "Weekly Occupancy View",
    "dashboard.highAvail": "High avail.",
    "dashboard.lowAvail": "Low",
    "dashboard.critical": "Critical",
    "dashboard.deliveries": "Deliveries",
    "dashboard.returnsLabel": "Returns",
    "dashboard.avgAvailability": "Average Availability",
    "dashboard.inventoryPerformance": "Inventory Performance",
    "dashboard.customRange": "Custom Range",
    "dashboard.selectDateRange": "Select a date range",
    "dashboard.analyzeHistory": "Analyze historical performance",
    "dashboard.applyFilter": "Apply Filter",
    "dashboard.today": "Today",
    "dashboard.thisWeek": "This Week",
    "dashboard.thisMonth": "This Month",
    "dashboard.mostRented": "Most Rented",
    "dashboard.mostProfitable": "Most Profitable",
    "dashboard.rentalsCount": "rentals",
    "dashboard.noData": "No data for this period",
    "dashboard.staleStock": "Idle Stock",
    "dashboard.idle": "not rented",
    "dashboard.allStockRotating": "All stock has rotation!",
    "dashboard.urgentAlerts": "Urgent Alerts",
    "dashboard.expiredRentals": "Expired rentals",
    "dashboard.viewReturns": "View returns",
    "dashboard.maintenance": "Maintenance",
    "dashboard.viewEquipment": "View equipment",
    
    // Rentals
    "rentals.title": "Active Rentals",
    "rentals.new": "New Rental",
    "rentals.customer": "Customer",
    "rentals.items": "Items",
    "rentals.days": "Days",
    "rentals.total": "Total",
    "rentals.status": "Status",
    "rentals.actions": "Actions",
    "rentals.changes": "CHANGES",
    "rentals.quickReturn": "Quick Return",
    "rentals.daysRemaining": "days remaining",
    
    // Returns
    "returns.title": "Returns",
    "returns.pending": "Pending Returns",
    "returns.process": "Process Return",
    "returns.manageChange": "MANAGE",
    
    // Ticket preview
    "ticket.rental": "RENTAL TICKET",
    "ticket.date": "Date",
    "ticket.customer": "Customer",
    "ticket.dni": "ID",
    "ticket.items": "Items",
    "ticket.subtotal": "Subtotal",
    "ticket.vat": "VAT",
    "ticket.total": "TOTAL",
    "ticket.vatIncluded": "VAT Included",
    "ticket.paymentMethod": "Payment Method",
    "ticket.cash": "Cash",
    "ticket.card": "Card",
    "ticket.thankYou": "Thank you for your trust!",
  },
  ca: {
    // Navigation
    "nav.dashboard": "Tauler",
    "nav.newRental": "Nou Lloguer",
    "nav.activeRentals": "Lloguers Actius",
    "nav.returns": "Devolucions",
    "nav.customers": "Clients",
    "nav.providers": "Proveïdors",
    "nav.inventory": "Inventari",
    "nav.tariffs": "Tarifes",
    "nav.reports": "Informes",
    "nav.maintenance": "Manteniment",
    "nav.cashRegister": "Caixa",
    "nav.integrations": "Integracions",
    "nav.settings": "Configuració",
    "nav.teamManagement": "Gestió d'Equip",
    "nav.storeManagement": "Gestió de Botigues",
    "nav.myAccount": "El Meu Compte",
    "nav.help": "Ajuda",
    "nav.support": "Suport",
    
    // Settings page
    "settings.title": "Configuració",
    "settings.subtitle": "Personalitza el comportament de l'aplicació",
    "settings.saveChanges": "Desar Canvis",
    "settings.saved": "Configuració desada correctament",
    
    // Brand Identity section
    "settings.brandIdentity": "Identitat de Marca",
    "settings.brandIdentity.desc": "Logotip i idioma de l'aplicació",
    "settings.logo": "Logotip de l'Empresa",
    "settings.logo.desc": "Apareixerà a la capçalera i als tiquets",
    "settings.logo.upload": "Pujar Logotip",
    "settings.logo.change": "Canviar Logotip",
    "settings.logo.remove": "Treure Logotip",
    "settings.logo.formats": "PNG, JPG o SVG. Màx 2MB",
    "settings.language": "Idioma",
    "settings.language.desc": "Selecciona l'idioma de la interfície",
    "settings.language.es": "Español",
    "settings.language.en": "English",
    "settings.language.ca": "Català",
    "settings.language.fr": "Français",
    
    // Ticket Design section
    "settings.ticketDesign": "Disseny de Tiquet",
    "settings.ticketDesign.desc": "Personalitza el contingut dels tiquets impresos",
    "settings.ticket.header": "Capçalera del Tiquet",
    "settings.ticket.header.placeholder": "Nom del negoci, adreça, telèfon...",
    "settings.ticket.footer": "Peu de Pàgina",
    "settings.ticket.footer.placeholder": "Missatge d'agraïment, horaris...",
    "settings.ticket.terms": "Termes Legals",
    "settings.ticket.terms.placeholder": "Condicions de lloguer, política de devolucions...",
    "settings.ticket.showDni": "Mostrar DNI del Client",
    "settings.ticket.showDni.desc": "El DNI apareixerà al tiquet imprès",
    "settings.ticket.showVat": "Mostrar Desglossament d'IVA",
    "settings.ticket.showVat.desc": "Mostra base imposable i IVA separats",
    "settings.ticket.preview": "Vista Prèvia del Tiquet",
    
    // VAT section
    "settings.vat": "Configuració d'IVA",
    "settings.vat.desc": "Defineix l'IVA aplicat als lloguers",
    "settings.vat.default": "IVA per Defecte",
    "settings.vat.default.desc": "Aquest percentatge s'aplicarà automàticament als nous lloguers",
    "settings.vat.included": "IVA Inclòs als Preus",
    "settings.vat.included.desc": "Els preus de tarifa ja inclouen l'IVA",

    // Currency section
    "settings.currency": "Moneda",
    "settings.currency.desc": "Moneda operativa de la botiga (només visualització)",
    "settings.currency.label": "Moneda de la botiga",
    "settings.currency.note": "Els imports no es converteixen: només canvia com es mostren. La subscripció de SkiFlow es factura sempre en EUR.",
    
    // Print section
    "settings.print": "Configuració d'Impressió",
    "settings.autoPrint": "Impressió Automàtica de Tiquets",
    "settings.autoPrint.desc": "El sistema imprimirà automàticament els tiquets en completar transaccions",
    "settings.autoPrint.enabled": "Els tiquets s'imprimiran automàticament",
    "settings.autoPrint.disabled": "Els tiquets requereixen confirmació manual",
    "settings.autoPrint.tip": "Activa aquesta opció si tens una impressora tèrmica connectada",
    
    // Hardware section
    "settings.hardware": "Maquinari",
    "settings.hardware.desc": "Configuració d'escàner i impressora",
    "settings.scanner": "Escàner / Pistola de Codis",
    "settings.scanner.quickMode": "Mode Escaneig Ràpid",
    "settings.scanner.quickMode.desc": "Afegeix el producte automàticament en detectar un codi exacte",
    "settings.scanner.quickMode.disabled": "Només cerca el producte i espera confirmació manual",
    "settings.printer": "Impressora",
    "settings.printer.paperWidth": "Amplada de Paper",
    "settings.printer.paperWidth.desc": "Selecciona l'amplada del paper de la teva impressora tèrmica",
    "settings.printer.80mm": "80mm (Estàndard)",
    "settings.printer.58mm": "58mm (Estret)",
    "settings.printer.autoPrint": "Auto-Imprimir",
    "settings.printer.autoPrint.desc": "Obre el diàleg d'impressió automàticament en confirmar un pagament",
    "settings.printer.doubleCopy": "Imprimir Doble Còpia",
    "settings.printer.doubleCopy.desc": "Imprimeix dos tiquets seguits (Botiga i Client)",
    "settings.printer.setupInstructions.title": "Configuració d'Impressora",
    "settings.printer.setupInstructions.step1": "Assegura't que la teva impressora tèrmica està instal·lada i marcada com a Predeterminada al teu ordinador.",
    "settings.printer.setupInstructions.step2": "Configura la mida del paper a la teva impressora (58mm o 80mm) segons el rotllo que facis servir.",
    "settings.printer.setupInstructions.step3": "Per impressió sense diàlegs, configura el teu navegador en 'Mode Quiosc' o marca 'Imprimir en silenci'.",
    "settings.printer.setupInstructions.note": "El sistema utilitza els controladors del teu sistema operatiu - no requereix configuració d'IP ni programari addicional.",
    
    // Common
    "common.cancel": "Cancel·lar",
    "common.save": "Desar",
    "common.close": "Tancar",
    "common.loading": "Carregant...",
    "common.status": "Estat actual",
    "common.recommended": "Recomanat",
    "common.preview": "Vista Prèvia",
    "common.enabled": "Activat",
    "common.disabled": "Desactivat",
    
    // Dashboard
    "dashboard.title": "Tauler de Control",
    "dashboard.subtitle": "Gestiona la teva botiga de lloguer amb informació en temps real",
    "dashboard.newRental": "NOU LLOGUER",
    "dashboard.revenueToday": "Ingressos Nets Avui",
    "dashboard.rentalsToday": "Lloguers Avui",
    "dashboard.activeRentals": "Lloguers Actius",
    "dashboard.activeRentals.desc": "En curs ara mateix",
    "dashboard.pendingReturns": "Devolucions Pendents",
    "dashboard.pendingReturns.allToday": "Totes per avui",
    "dashboard.pendingReturns.none": "Sense pendents",
    "dashboard.pendingReturns.todayAndOverdue": "Avui + endarrerides",
    "dashboard.overdueReturns": "Devolucions Endarrerides",
    "dashboard.stockOccupation": "Ocupació Stock",
    "dashboard.rentedMaterial": "Material llogat",
    "dashboard.dailyCustomers": "Clients del Dia",
    "dashboard.uniqueCustomers": "Clients únics atesos",
    "dashboard.returnsControl": "Control de Devolucions",
    "dashboard.returnsControl.desc": "Material pendent de recollir AVUI",
    "dashboard.delay": "RETARD!",
    "dashboard.items": "articles",
    "dashboard.toReturn": "per tornar",
    "dashboard.viewList": "Veure llista",
    "dashboard.overdueWarning": "i hi ha material sense tornar",
    "dashboard.manageNow": "Gestionar ara",
    "dashboard.weeklyOccupation": "Vista Setmanal d'Ocupació",
    "dashboard.highAvail": "Alta disp.",
    "dashboard.lowAvail": "Pocs",
    "dashboard.critical": "Crític",
    "dashboard.deliveries": "Entregues",
    "dashboard.returnsLabel": "Devolucions",
    "dashboard.avgAvailability": "Disponibilitat Mitjana",
    "dashboard.inventoryPerformance": "Rendiment d'Inventari",
    "dashboard.customRange": "Rang Personalitzat",
    "dashboard.selectDateRange": "Selecciona un rang de dates",
    "dashboard.analyzeHistory": "Analitza el rendiment històric",
    "dashboard.applyFilter": "Aplicar Filtre",
    "dashboard.today": "Avui",
    "dashboard.thisWeek": "Aquesta Setmana",
    "dashboard.thisMonth": "Aquest Mes",
    "dashboard.mostRented": "Més Llogats",
    "dashboard.mostProfitable": "Més Rendibles",
    "dashboard.rentalsCount": "lloguers",
    "dashboard.noData": "Sense dades per aquest període",
    "dashboard.staleStock": "Stock Aturat",
    "dashboard.idle": "sense llogar",
    "dashboard.allStockRotating": "Tot l'stock té rotació!",
    "dashboard.urgentAlerts": "Alertes Urgents",
    "dashboard.expiredRentals": "Lloguers vençuts",
    "dashboard.viewReturns": "Veure devolucions",
    "dashboard.maintenance": "Manteniment",
    "dashboard.viewEquipment": "Veure equips",
    
    // Rentals
    "rentals.title": "Lloguers Actius",
    "rentals.new": "Nou Lloguer",
    "rentals.customer": "Client",
    "rentals.items": "Articles",
    "rentals.days": "Dies",
    "rentals.total": "Total",
    "rentals.status": "Estat",
    "rentals.actions": "Accions",
    "rentals.changes": "CANVIS",
    "rentals.quickReturn": "Devolució Ràpida",
    "rentals.daysRemaining": "dies restants",
    
    // Returns
    "returns.title": "Devolucions",
    "returns.pending": "Pendents de Devolució",
    "returns.process": "Processar Devolució",
    "returns.manageChange": "GESTIONAR",
    
    // Ticket preview
    "ticket.rental": "TIQUET DE LLOGUER",
    "ticket.date": "Data",
    "ticket.customer": "Client",
    "ticket.dni": "DNI",
    "ticket.items": "Articles",
    "ticket.subtotal": "Subtotal",
    "ticket.vat": "IVA",
    "ticket.total": "TOTAL",
    "ticket.vatIncluded": "IVA Inclòs",
    "ticket.paymentMethod": "Mètode de Pagament",
    "ticket.cash": "Efectiu",
    "ticket.card": "Targeta",
    "ticket.thankYou": "Gràcies per la vostra confiança!",
  },
  fr: {
    // Navigation
    "nav.dashboard": "Tableau de Bord",
    "nav.newRental": "Nouvelle Location",
    "nav.activeRentals": "Locations Actives",
    "nav.returns": "Retours",
    "nav.customers": "Clients",
    "nav.providers": "Fournisseurs",
    "nav.inventory": "Inventaire",
    "nav.tariffs": "Tarifs",
    "nav.reports": "Rapports",
    "nav.maintenance": "Maintenance",
    "nav.cashRegister": "Caisse",
    "nav.integrations": "Intégrations",
    "nav.settings": "Configuration",
    "nav.teamManagement": "Gestion d'Équipe",
    "nav.storeManagement": "Gestion des Magasins",
    "nav.myAccount": "Mon Compte",
    "nav.help": "Aide",
    "nav.support": "Support",
    
    // Settings page
    "settings.title": "Configuration",
    "settings.subtitle": "Personnalisez le comportement de l'application",
    "settings.saveChanges": "Enregistrer",
    "settings.saved": "Configuration enregistrée avec succès",
    
    // Brand Identity section
    "settings.brandIdentity": "Identité de Marque",
    "settings.brandIdentity.desc": "Logo et langue de l'application",
    "settings.logo": "Logo de l'Entreprise",
    "settings.logo.desc": "Apparaîtra dans l'en-tête et sur les tickets",
    "settings.logo.upload": "Télécharger Logo",
    "settings.logo.change": "Changer Logo",
    "settings.logo.remove": "Supprimer Logo",
    "settings.logo.formats": "PNG, JPG ou SVG. Max 2Mo",
    "settings.language": "Langue",
    "settings.language.desc": "Sélectionnez la langue de l'interface",
    "settings.language.es": "Español",
    "settings.language.en": "English",
    "settings.language.ca": "Català",
    "settings.language.fr": "Français",
    
    // Ticket Design section
    "settings.ticketDesign": "Design du Ticket",
    "settings.ticketDesign.desc": "Personnalisez le contenu des tickets imprimés",
    "settings.ticket.header": "En-tête du Ticket",
    "settings.ticket.header.placeholder": "Nom de l'entreprise, adresse, téléphone...",
    "settings.ticket.footer": "Pied de Page",
    "settings.ticket.footer.placeholder": "Message de remerciement, horaires...",
    "settings.ticket.terms": "Conditions Légales",
    "settings.ticket.terms.placeholder": "Conditions de location, politique de retour...",
    "settings.ticket.showDni": "Afficher l'ID du Client",
    "settings.ticket.showDni.desc": "L'ID apparaîtra sur le ticket imprimé",
    "settings.ticket.showVat": "Afficher le Détail de la TVA",
    "settings.ticket.showVat.desc": "Affiche la base imposable et la TVA séparément",
    "settings.ticket.preview": "Aperçu du Ticket",
    
    // VAT section
    "settings.vat": "Configuration de la TVA",
    "settings.vat.desc": "Définissez la TVA appliquée aux locations",
    "settings.vat.default": "TVA par Défaut",
    "settings.vat.default.desc": "Ce pourcentage sera automatiquement appliqué aux nouvelles locations",
    "settings.vat.included": "TVA Incluse dans les Prix",
    "settings.vat.included.desc": "Les prix des tarifs incluent déjà la TVA",

    // Currency section
    "settings.currency": "Devise",
    "settings.currency.desc": "Devise opérationnelle du magasin (affichage uniquement)",
    "settings.currency.label": "Devise du magasin",
    "settings.currency.note": "Les montants ne sont pas convertis : seul l'affichage change. L'abonnement SkiFlow est toujours facturé en EUR.",
    
    // Print section
    "settings.print": "Configuration d'Impression",
    "settings.autoPrint": "Impression Automatique des Tickets",
    "settings.autoPrint.desc": "Le système imprimera automatiquement les tickets lors de la finalisation des transactions",
    "settings.autoPrint.enabled": "Les tickets seront imprimés automatiquement",
    "settings.autoPrint.disabled": "Les tickets nécessitent une confirmation manuelle",
    "settings.autoPrint.tip": "Activez cette option si vous avez une imprimante thermique connectée",
    
    // Hardware section
    "settings.hardware": "Matériel",
    "settings.hardware.desc": "Configuration du scanner et de l'imprimante",
    "settings.scanner": "Scanner / Pistolet à Codes",
    "settings.scanner.quickMode": "Mode Scan Rapide",
    "settings.scanner.quickMode.desc": "Ajoute automatiquement le produit lorsqu'un code exact est détecté",
    "settings.scanner.quickMode.disabled": "Recherche uniquement le produit et attend la confirmation manuelle",
    "settings.printer": "Imprimante",
    "settings.printer.paperWidth": "Largeur du Papier",
    "settings.printer.paperWidth.desc": "Sélectionnez la largeur du papier de votre imprimante thermique",
    "settings.printer.80mm": "80mm (Standard)",
    "settings.printer.58mm": "58mm (Étroit)",
    "settings.printer.autoPrint": "Auto-Imprimer",
    "settings.printer.autoPrint.desc": "Ouvre automatiquement la boîte de dialogue d'impression lors de la confirmation d'un paiement",
    "settings.printer.doubleCopy": "Imprimer Double Copie",
    "settings.printer.doubleCopy.desc": "Imprime deux tickets à la suite (Magasin et Client)",
    "settings.printer.setupInstructions.title": "Configuration de l'Imprimante",
    "settings.printer.setupInstructions.step1": "Assurez-vous que votre imprimante thermique est installée et définie comme Par défaut sur votre ordinateur.",
    "settings.printer.setupInstructions.step2": "Configurez la taille du papier sur votre imprimante (58mm ou 80mm) selon votre rouleau.",
    "settings.printer.setupInstructions.step3": "Pour l'impression silencieuse, configurez votre navigateur en 'Mode Kiosque' ou activez 'Imprimer silencieusement'.",
    "settings.printer.setupInstructions.note": "Le système utilise les pilotes de votre système d'exploitation - aucune configuration IP ni logiciel supplémentaire requis.",
    
    // Common
    "common.cancel": "Annuler",
    "common.save": "Enregistrer",
    "common.close": "Fermer",
    "common.loading": "Chargement...",
    "common.status": "État actuel",
    "common.recommended": "Recommandé",
    "common.preview": "Aperçu",
    "common.enabled": "Activé",
    "common.disabled": "Désactivé",
    
    // Dashboard
    "dashboard.title": "Tableau de Bord",
    "dashboard.subtitle": "Gérez votre magasin de location avec des informations en temps réel",
    "dashboard.newRental": "NOUVELLE LOCATION",
    "dashboard.revenueToday": "Revenus Nets Aujourd'hui",
    "dashboard.rentalsToday": "Locations Aujourd'hui",
    "dashboard.activeRentals": "Locations Actives",
    "dashboard.activeRentals.desc": "En cours actuellement",
    "dashboard.pendingReturns": "Retours en Attente",
    "dashboard.pendingReturns.allToday": "Tous pour aujourd'hui",
    "dashboard.pendingReturns.none": "Aucun en attente",
    "dashboard.pendingReturns.todayAndOverdue": "Aujourd'hui + en retard",
    "dashboard.overdueReturns": "Retours en Retard",
    "dashboard.stockOccupation": "Occupation du Stock",
    "dashboard.rentedMaterial": "Matériel loué",
    "dashboard.dailyCustomers": "Clients du Jour",
    "dashboard.uniqueCustomers": "Clients uniques servis",
    "dashboard.returnsControl": "Contrôle des Retours",
    "dashboard.returnsControl.desc": "Matériel en attente de récupération AUJOURD'HUI",
    "dashboard.delay": "EN RETARD!",
    "dashboard.items": "articles",
    "dashboard.toReturn": "à retourner",
    "dashboard.viewList": "Voir la liste",
    "dashboard.overdueWarning": "et il y a du matériel non retourné",
    "dashboard.manageNow": "Gérer maintenant",
    "dashboard.weeklyOccupation": "Vue Hebdomadaire d'Occupation",
    "dashboard.highAvail": "Haute disp.",
    "dashboard.lowAvail": "Peu",
    "dashboard.critical": "Critique",
    "dashboard.deliveries": "Livraisons",
    "dashboard.returnsLabel": "Retours",
    "dashboard.avgAvailability": "Disponibilité Moyenne",
    "dashboard.inventoryPerformance": "Performance de l'Inventaire",
    "dashboard.customRange": "Plage Personnalisée",
    "dashboard.selectDateRange": "Sélectionnez une plage de dates",
    "dashboard.analyzeHistory": "Analysez la performance historique",
    "dashboard.applyFilter": "Appliquer le Filtre",
    "dashboard.today": "Aujourd'hui",
    "dashboard.thisWeek": "Cette Semaine",
    "dashboard.thisMonth": "Ce Mois",
    "dashboard.mostRented": "Plus Loués",
    "dashboard.mostProfitable": "Plus Rentables",
    "dashboard.rentalsCount": "locations",
    "dashboard.noData": "Aucune donnée pour cette période",
    "dashboard.staleStock": "Stock Inactif",
    "dashboard.idle": "non loué",
    "dashboard.allStockRotating": "Tout le stock est en rotation!",
    "dashboard.urgentAlerts": "Alertes Urgentes",
    "dashboard.expiredRentals": "Locations expirées",
    "dashboard.viewReturns": "Voir les retours",
    "dashboard.maintenance": "Maintenance",
    "dashboard.viewEquipment": "Voir les équipements",
    
    // Rentals
    "rentals.title": "Locations Actives",
    "rentals.new": "Nouvelle Location",
    "rentals.customer": "Client",
    "rentals.items": "Articles",
    "rentals.days": "Jours",
    "rentals.total": "Total",
    "rentals.status": "Statut",
    "rentals.actions": "Actions",
    "rentals.changes": "CHANGEMENTS",
    "rentals.quickReturn": "Retour Rapide",
    "rentals.daysRemaining": "jours restants",
    
    // Returns
    "returns.title": "Retours",
    "returns.pending": "En Attente de Retour",
    "returns.process": "Traiter le Retour",
    "returns.manageChange": "GÉRER",
    
    // Ticket preview
    "ticket.rental": "TICKET DE LOCATION",
    "ticket.date": "Date",
    "ticket.customer": "Client",
    "ticket.dni": "ID",
    "ticket.items": "Articles",
    "ticket.subtotal": "Sous-total",
    "ticket.vat": "TVA",
    "ticket.total": "TOTAL",
    "ticket.vatIncluded": "TVA Incluse",
    "ticket.paymentMethod": "Mode de Paiement",
    "ticket.cash": "Espèces",
    "ticket.card": "Carte",
    "ticket.thankYou": "Merci pour votre confiance!",
  }
};

export function SettingsProvider({ children }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  
  // Flag para evitar múltiples cargas
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // ========== BASIC SETTINGS ==========
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });
  
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'es';
  });

  // ========== OPERATIONAL CURRENCY (display only, per store) ==========
  const [currency, setCurrencyState] = useState(() => {
    return localStorage.getItem('store_currency') || 'EUR';
  });
  const setCurrency = (code) => {
    setActiveCurrency(code);
    setCurrencyState(code);
  };
  
  const [autoPrint, setAutoPrint] = useState(() => {
    // Default ON on a fresh install. `null` = never toggled → true.
    const saved = localStorage.getItem('auto_print_enabled');
    return saved === null ? true : saved === 'true';
  });

  // ========== BRAND IDENTITY ==========
  const [companyLogo, setCompanyLogo] = useState(() => {
    try {
      const savedLogo = localStorage.getItem('company_logo');
      if (savedLogo) {
        const sizeKB = Math.round((savedLogo.length * 0.75) / 1024);
        console.log(`[Settings] Logo cargado desde localStorage (${sizeKB}KB)`);
        return savedLogo;
      }
      console.log('[Settings] No hay logo guardado en localStorage');
      return null;
    } catch (error) {
      console.error('[Settings] Error cargando logo desde localStorage:', error);
      return null;
    }
  });

  // ========== CARGAR SETTINGS DESDE BACKEND AL INICIAR ==========
  useEffect(() => {
    const loadSettingsFromBackend = async () => {
      // Cookie-only: enviamos la cookie httpOnly via credentials:'include'.
      // Si no hay sesión, el backend responde 401 y el interceptor global
      // gestiona el logout. Un Bearer 'null' es ignorado por el backend.
      const token = localStorage.getItem('token');
      
      try {
        console.log('[Settings] Cargando configuración desde backend...');
        const response = await fetch(`${API}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        
        if (response.ok) {
          const settings = await response.json();
          console.log('[Settings] Configuración cargada desde backend:', settings);
          
          // Solo actualizar si hay datos en el backend
          if (settings.company_logo !== undefined) {
            // ALWAYS update logo from backend (source of truth per store)
            setCompanyLogo(settings.company_logo || null);
            if (settings.company_logo) {
              localStorage.setItem('company_logo', settings.company_logo);
            } else {
              localStorage.removeItem('company_logo');
            }
          }
          if (settings.ticket_header) setTicketHeader(settings.ticket_header);
          if (settings.ticket_footer) setTicketFooter(settings.ticket_footer);
          if (settings.ticket_terms) setTicketTerms(settings.ticket_terms);
          if (settings.show_dni_on_ticket !== undefined) setShowDniOnTicket(settings.show_dni_on_ticket);
          if (settings.show_vat_on_ticket !== undefined) setShowVatOnTicket(settings.show_vat_on_ticket);
          if (settings.default_vat !== undefined) setDefaultVat(settings.default_vat);
          if (settings.vat_included_in_prices !== undefined) setVatIncludedInPrices(settings.vat_included_in_prices);
          if (settings.currency) setCurrency(settings.currency);
          
          // ========== HARDWARE SETTINGS FROM BACKEND (source of truth) ==========
          // Always apply backend values - they are the source of truth per store
          if (settings.auto_print_ticket !== undefined) {
            setAutoPrint(settings.auto_print_ticket);
            localStorage.setItem('auto_print_enabled', settings.auto_print_ticket.toString());
          }

          if (settings.quick_scan_mode !== undefined) {
            setQuickScanMode(settings.quick_scan_mode);
            localStorage.setItem('quick_scan_mode', settings.quick_scan_mode.toString());
          }

          // FIX: previously read `auto_print_ticket` here → the payment
          // toggle mirrored the ticket toggle, so users could never turn
          // one off without turning both off.
          if (settings.auto_print_on_payment !== undefined) {
            setAutoPrintOnPayment(settings.auto_print_on_payment);
            localStorage.setItem('auto_print_on_payment', settings.auto_print_on_payment.toString());
          }

          if (settings.ticket_paper_width !== undefined) {
            setPaperWidth(settings.ticket_paper_width);
            localStorage.setItem('paper_width', settings.ticket_paper_width);
          }
          
          setSettingsLoaded(true);
        }
      } catch (error) {
        console.error('[Settings] Error cargando desde backend:', error);
      }
    };
    
    // Pequeño delay para asegurar que el token existe
    setTimeout(loadSettingsFromBackend, 1000);
    
    // Re-load settings when token changes (login/logout/store switch)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          setTimeout(loadSettingsFromBackend, 500);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [API]);

  // ========== TICKET DESIGN ==========
  const [ticketHeader, setTicketHeader] = useState(() => {
    return localStorage.getItem('ticket_header') || 'ALPINE SKI RENTAL\nCalle Principal 123\nTel: 612 345 678';
  });
  
  const [ticketFooter, setTicketFooter] = useState(() => {
    return localStorage.getItem('ticket_footer') || '¡Gracias por su visita!\nHorario: 9:00 - 20:00';
  });
  
  const [ticketTerms, setTicketTerms] = useState(() => {
    return localStorage.getItem('ticket_terms') || 'El cliente se compromete a devolver el material en las mismas condiciones. Cualquier daño será facturado según tarifa vigente.';
  });
  
  const [showDniOnTicket, setShowDniOnTicket] = useState(() => {
    const saved = localStorage.getItem('show_dni_on_ticket');
    return saved !== 'false'; // Default true
  });
  
  const [showVatOnTicket, setShowVatOnTicket] = useState(() => {
    const saved = localStorage.getItem('show_vat_on_ticket');
    return saved === 'true';
  });

  // ========== VAT CONFIGURATION ==========
  const [defaultVat, setDefaultVat] = useState(() => {
    const saved = localStorage.getItem('default_vat');
    return saved ? parseFloat(saved) : 21;
  });
  
  const [vatIncludedInPrices, setVatIncludedInPrices] = useState(() => {
    const saved = localStorage.getItem('vat_included_in_prices');
    return saved !== 'false'; // Default true
  });

  // ========== HARDWARE SETTINGS ==========
  // Scanner / Barcode Gun
  const [quickScanMode, setQuickScanMode] = useState(() => {
    // Default ON on a fresh install (any USB HID scanner works out of the box).
    const saved = localStorage.getItem('quick_scan_mode');
    return saved === null ? true : saved === 'true';
  });

  // Printer
  const [paperWidth, setPaperWidth] = useState(() => {
    const saved = localStorage.getItem('paper_width');
    return saved || '80mm';
  });

  const [autoPrintOnPayment, setAutoPrintOnPayment] = useState(() => {
    // Default ON on a fresh install — most POS workflows want a receipt
    // automatically on successful payment.
    const saved = localStorage.getItem('auto_print_on_payment');
    return saved === null ? true : saved === 'true';
  });

  // Print mode: universal browser path (default) vs. thermal ESC/POS via
  // QZ Tray. Persisted in localStorage so it survives reloads without
  // waiting for the backend fetch.
  const [printMode, setPrintMode] = useState(() => {
    return localStorage.getItem('print_mode') || 'browser';
  });

  // Selected thermal printer name (only meaningful when printMode === 'escpos').
  const [qzPrinterName, setQzPrinterName] = useState(() => {
    return localStorage.getItem('qz_printer_name') || '';
  });

  const [printDoubleCopy, setPrintDoubleCopy] = useState(() => {
    const saved = localStorage.getItem('print_double_copy');
    return saved === 'true';
  });

  // ========== EFFECTS FOR PERSISTENCE ==========
  
  // Dark mode - Global application
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    
    if (darkMode) {
      root.classList.add('dark');
      body.classList.add('dark');
      // Set explicit colors for better compatibility
      body.style.backgroundColor = '#121212';
      body.style.color = '#f0f0f0';
      // Add data attribute for CSS targeting
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      body.classList.remove('dark');
      body.style.backgroundColor = '#f8fafc';
      body.style.color = '#0f172a';
      root.setAttribute('data-theme', 'light');
    }
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  // Language
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Operational currency
  useEffect(() => {
    localStorage.setItem('store_currency', currency);
    setActiveCurrency(currency);
  }, [currency]);

  // Auto print
  useEffect(() => {
    localStorage.setItem('auto_print_enabled', autoPrint.toString());
  }, [autoPrint]);

  // Company logo - con manejo de errores para localStorage
  useEffect(() => {
    try {
      if (companyLogo) {
        // Verificar tamaño antes de guardar
        const sizeKB = (companyLogo.length * 0.75) / 1024;
        console.log(`[Settings] Guardando logo (${Math.round(sizeKB)}KB)`);
        
        if (sizeKB > 500) {
          console.warn('[Settings] Logo demasiado grande para localStorage, podría fallar');
        }
        
        localStorage.setItem('company_logo', companyLogo);
        console.log('[Settings] Logo guardado en localStorage correctamente');
      } else {
        localStorage.removeItem('company_logo');
        console.log('[Settings] Logo eliminado de localStorage');
      }
    } catch (error) {
      console.error('[Settings] Error guardando logo en localStorage:', error);
      // Si es error de cuota, intentar con una versión más comprimida podría ayudar
      if (error.name === 'QuotaExceededError') {
        console.error('[Settings] localStorage lleno - no se puede guardar el logo');
      }
    }
  }, [companyLogo]);

  // Ticket design
  useEffect(() => {
    localStorage.setItem('ticket_header', ticketHeader);
  }, [ticketHeader]);

  useEffect(() => {
    localStorage.setItem('ticket_footer', ticketFooter);
  }, [ticketFooter]);

  useEffect(() => {
    localStorage.setItem('ticket_terms', ticketTerms);
  }, [ticketTerms]);

  useEffect(() => {
    localStorage.setItem('show_dni_on_ticket', showDniOnTicket.toString());
  }, [showDniOnTicket]);

  useEffect(() => {
    localStorage.setItem('show_vat_on_ticket', showVatOnTicket.toString());
  }, [showVatOnTicket]);

  // VAT
  useEffect(() => {
    localStorage.setItem('default_vat', defaultVat.toString());
  }, [defaultVat]);

  useEffect(() => {
    localStorage.setItem('vat_included_in_prices', vatIncludedInPrices.toString());
  }, [vatIncludedInPrices]);

  // Hardware - Scanner
  useEffect(() => {
    localStorage.setItem('quick_scan_mode', quickScanMode.toString());
  }, [quickScanMode]);

  // Hardware - Printer
  useEffect(() => {
    localStorage.setItem('paper_width', paperWidth);
  }, [paperWidth]);

  useEffect(() => {
    localStorage.setItem('auto_print_on_payment', autoPrintOnPayment.toString());
  }, [autoPrintOnPayment]);

  useEffect(() => {
    localStorage.setItem('print_double_copy', printDoubleCopy.toString());
  }, [printDoubleCopy]);

  useEffect(() => {
    localStorage.setItem('print_mode', printMode);
  }, [printMode]);

  useEffect(() => {
    if (qzPrinterName) localStorage.setItem('qz_printer_name', qzPrinterName);
    else localStorage.removeItem('qz_printer_name');
  }, [qzPrinterName]);

  // ── LIVE HARDWARE SYNC TO BACKEND ─────────────────────────────────────
  // The hardware section in Settings applies changes in real time (no
  // "Save" button). We debounce backend writes so a burst of toggles
  // only sends one request.
  const hardwareSyncTimerRef = useRef(null);
  useEffect(() => {
    if (!settingsLoaded) return; // don't push before we've loaded from server
    if (hardwareSyncTimerRef.current) clearTimeout(hardwareSyncTimerRef.current);
    hardwareSyncTimerRef.current = setTimeout(() => {
      saveSettingsToBackendRef.current?.();
    }, 600);
    return () => {
      if (hardwareSyncTimerRef.current) clearTimeout(hardwareSyncTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickScanMode, paperWidth, autoPrintOnPayment, autoPrint, settingsLoaded]);

  // Translation function
  const t = (key) => {
    return translations[language]?.[key] || translations['es'][key] || key;
  };

  // Calculate VAT amounts
  const calculateVat = (totalWithVat) => {
    if (vatIncludedInPrices) {
      const base = totalWithVat / (1 + defaultVat / 100);
      const vat = totalWithVat - base;
      return { base: base, vat: vat, total: totalWithVat };
    } else {
      const vat = totalWithVat * (defaultVat / 100);
      return { base: totalWithVat, vat: vat, total: totalWithVat + vat };
    }
  };

  // Función para guardar todas las settings en el backend
  const saveSettingsToBackendRef = useRef(null);
  const saveSettingsToBackend = async () => {
    // Cookie-only: la cookie httpOnly va con credentials:'include'
    const token = localStorage.getItem('token');
    
    try {
      console.log('[Settings] Guardando configuración en backend...');
      const payload = {
        company_logo: companyLogo,
        ticket_header: ticketHeader,
        ticket_footer: ticketFooter,
        ticket_terms: ticketTerms,
        show_dni_on_ticket: showDniOnTicket,
        show_vat_on_ticket: showVatOnTicket,
        default_vat: defaultVat,
        vat_included_in_prices: vatIncludedInPrices,
        currency: currency,
        language: language,
        dark_mode: darkMode,
        auto_print: autoPrint,
        auto_print_ticket: autoPrint,
        quick_scan_mode: quickScanMode,
        auto_print_on_payment: autoPrintOnPayment,
        ticket_paper_width: paperWidth
      };
      
      const response = await fetch(`${API}/api/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        console.log('[Settings] Configuración guardada en backend');
        return true;
      } else {
        console.error('[Settings] Error guardando en backend:', response.status);
        return false;
      }
    } catch (error) {
      console.error('[Settings] Error guardando en backend:', error);
      return false;
    }
  };
  // Keep the ref pointing at the latest closure so debounced timers can
  // call the up-to-date function without adding it as a dependency.
  saveSettingsToBackendRef.current = saveSettingsToBackend;

  const value = {
    // Basic
    darkMode, setDarkMode,
    language, setLanguage,
    autoPrint, setAutoPrint,
    t, translations,
    
    // Brand
    companyLogo, setCompanyLogo,
    
    // Ticket Design
    ticketHeader, setTicketHeader,
    ticketFooter, setTicketFooter,
    ticketTerms, setTicketTerms,
    showDniOnTicket, setShowDniOnTicket,
    showVatOnTicket, setShowVatOnTicket,
    
    // VAT
    defaultVat, setDefaultVat,
    vatIncludedInPrices, setVatIncludedInPrices,
    calculateVat,

    // Operational currency
    currency, setCurrency,

    // Hardware
    quickScanMode, setQuickScanMode,
    paperWidth, setPaperWidth,
    autoPrintOnPayment, setAutoPrintOnPayment,
    printMode, setPrintMode,
    qzPrinterName, setQzPrinterName,
    printDoubleCopy, setPrintDoubleCopy,
    
    // Backend sync
    saveSettingsToBackend,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export default SettingsContext;
